// @ts-strict-ignore
import type { ApplicationTypes } from '@usealloy/typegen';
import type {
  Action, DataRoute, PublicId, Threshold,
} from 'flow/store/engine/types';
import type { WorkflowState } from 'flow/store/redux/state';
import type { WorkflowSchema } from '~/helpers/api-new/modules/workflows/workflowsApi';

export interface SnippetIntegration {
  internal_name: string;
  display_name: string;
}

export type SnippetRegion =
  | 'Asia'
  | 'CA'
  | 'Europe'
  | 'LatinAmerica'
  | 'NorthAmerica'
  | 'UnitedKingdom'
  | 'US'
  | string;

// todo BUILD-3895 - consolidate this definition
export interface SnippetMetadata {
  archived_at: string | null; // ISO string
  categories?: string[];
  date_created: string; // ISO string
  date_updated: string; // ISO string
  description: string;
  id: string; // UUID
  integrations: SnippetIntegration[];
  name: string;
  published_version: number | null;
  regions: SnippetRegion[];
  scope: SnippetScope;
  token: string;
  workflow_type: ApplicationTypes;
  workflow_taxonomy_type?: string | null;
}

export interface SnippetVersionMetadata {
  changelog?: string;
  created_at: string | null; // ISO date string
  created_by: string | null;
  integrations: SnippetIntegration[];
  notes?: string;
  parent_version: number | null;
  published_at: string | null; // ISO date string
  published_by: string | null;
  token: string;
  version: number;
}

export type SnippetData = WorkflowSchema;

export interface SnippetWithPublishedVersion {
  snippet_metadata: SnippetMetadata;
  published_version_metadata: SnippetVersionMetadata | null;
}

export interface SnippetVersionProps {
  tags: any[];
  outcomes: any[];
  services: any[];
  publishedAttributes: any[];
  reasonCodeGroups: any[];
  lists: any[];
  customLists: any[];
  globalServiceConfigOptions: any[];
  universalAttributes: any[];
  snippets: any[];
}

// todo BUILD-3895 - consolidate this definition
export interface SnippetDetails {
  snippet_data: SnippetData;
  snippet_metadata: SnippetMetadata;
  snippet_version_metadata: SnippetVersionMetadata;
}

export interface SnippetVersionDetails {
  snippet_version_metadata: SnippetVersionMetadata;
  snippet_version_data: SnippetData;
  snippet_version_props: SnippetVersionProps;
}

export type SnippetVersion = {
  data: {
    actions: WorkflowState['workflowData']['actions'];
    thresholds: WorkflowState['workflowData']['thresholds'];
    dataRoutes: WorkflowState['workflowData']['dataRoutes'];
  };
  props: {
    tags: WorkflowState['workflowProps']['tags'];
    outcomes: WorkflowState['workflowProps']['outcomes'];
    services: Array<WorkflowState['workflowProps']['services'][string]>;
    publishedAttributes: WorkflowState['workflowProps']['publishedAttributes'];
    reasonCodeGroups: WorkflowState['workflowProps']['reasonCodeGroups'];
    lists: WorkflowState['workflowProps']['lists'];
    customLists: WorkflowState['workflowProps']['customLists'];
    globalServiceConfigOptions: WorkflowState['workflowProps']['globalServiceConfigOptions'];
    universalAttributes: Array<WorkflowState['workflowProps']['universalAttributes'][number]>;
    snippets: Array<WorkflowState['workflowProps']['snippets'][string]>;
  };
  metadata: null | {
    token: string;
    version: number;
    integrations: SnippetIntegration[];
    publishedBy: string | null;
    // todo - change this to ISO string
    publishedAt: Date | null;
    createdBy: string | null;
    // todo - change this to ISO string
    createdAt: Date | null;
    changeLog?: string;
    notes?: string;
  };
};


// todo - we could abstract this to be usable in more contexts
export interface WFElementOperationMap {
  added: Set<PublicId>;
  modified: Set<PublicId>;
  removed: Set<PublicId>;
  noop: Set<PublicId>;
}

// todo - finalize this API and account for the rest of the editor
export type WFElement = Action | Threshold | DataRoute;

export const SNIPPET_SCOPE = {
  GLOBAL: 'global',
  INTERNAL: 'internal',
  LOCAL: 'local',
} as const;

export type SnippetScope = typeof SNIPPET_SCOPE[keyof typeof SNIPPET_SCOPE];
